import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { VehicleProfile } from '../../../types/Vehicle';
import vehicleProfileService from '../../../services/vehicleProfileService';
import VehicleProfileList from '../components/VehicleProfileList';
import VehicleProfileForm from '../components/VehicleProfileForm';
import ServerPagination from '../../../components/ServerPagination';
import { usePermissions } from '../../../hooks/usePermissions';
import { Button, Spinner } from '@/components/ui';

const VehicleProfilesPage: React.FC = () => {
  const { hasPermission } = usePermissions();
  const [searchParams, setSearchParams] = useSearchParams();
  const [profiles, setProfiles] = useState<VehicleProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [showProfileForm, setShowProfileForm] = useState(false);
  const [editingProfile, setEditingProfile] = useState<VehicleProfile | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 20,
    totalCount: 0,
    totalPages: 0,
    hasNext: false,
    hasPrevious: false
  });

  const canEdit = hasPermission('vehicle:profile:manage') || hasPermission('vehicle:edit');

  useEffect(() => {
    const pageFromUrl = parseInt(searchParams.get('page') || '1');
    const pageSizeFromUrl = parseInt(searchParams.get('pageSize') || '20');
    setPagination(prev => ({ ...prev, page: pageFromUrl, pageSize: pageSizeFromUrl }));
  }, [searchParams]);

  useEffect(() => {
    fetchProfiles();
  }, [pagination.page, pagination.pageSize]);

  const updateUrlParams = (newPagination?: Partial<typeof pagination>) => {
    const params = new URLSearchParams(searchParams);
    
    if (newPagination) {
      if (newPagination.page) params.set('page', newPagination.page.toString());
      if (newPagination.pageSize) params.set('pageSize', newPagination.pageSize.toString());
    }
    
    setSearchParams(params);
  };

  const fetchProfiles = async () => {
    try {
      setLoading(true);
      const response = await vehicleProfileService.getProfilesPaginated(pagination.page, pagination.pageSize);
      setProfiles(response.data);
      setPagination(prev => ({ ...prev, ...response.pagination }));
    } catch (error) {
      console.error('Error fetching profiles:', error);
      alert('Failed to load vehicle profiles');
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (page: number) => {
    updateUrlParams({ page });
  };

  const handlePageSizeChange = (pageSize: number) => {
    updateUrlParams({ page: 1, pageSize });
  };

  const handleCreateProfile = () => {
    setEditingProfile(null);
    setShowProfileForm(true);
  };

  const handleEditProfile = (profile: VehicleProfile) => {
    setEditingProfile(profile);
    setShowProfileForm(true);
  };

  const handleDeleteProfile = async (profileId: number) => {
    if (!window.confirm('Are you sure you want to delete this profile? Vehicles using this profile will need to be reassigned.')) {
      return;
    }

    try {
      await vehicleProfileService.deleteProfile(profileId);
      await fetchProfiles();
    } catch (error: any) {
      alert(error.message || 'Failed to delete profile');
    }
  };

  const handleProfileFormClose = () => {
    setShowProfileForm(false);
    setEditingProfile(null);
    fetchProfiles();
  };

  if (loading) {
    return <div className="tw-w-full tw-p-4">Loading...</div>;
  }

  return (
    <div className="tw-w-full tw-p-4">
      <div className="tw-flex tw-justify-between tw-items-center tw-mb-4">
        <h1>Vehicle Profiles</h1>
        {canEdit && (
          <Button variant="primary" onClick={handleCreateProfile}>
            <i className="bi bi-plus-circle tw-mr-1"></i>Create Profile
          </Button>
        )}
      </div>

      <p className="tw-text-content-muted tw-mb-4">
        Manage vehicle inspection profiles. Profiles define inspection requirements and settings for different vehicle classifications.
      </p>

      {loading ? (
        <div className="tw-text-center tw-py-5">
          <Spinner role="status">
            <span className="tw-sr-only">Loading...</span>
          </Spinner>
        </div>
      ) : (
        <>
          <VehicleProfileList
            profiles={profiles}
            onEdit={handleEditProfile}
            onDelete={handleDeleteProfile}
            canEdit={canEdit}
          />
          <ServerPagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            totalCount={pagination.totalCount}
            pageSize={pagination.pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </>
      )}

      {showProfileForm && (
        <VehicleProfileForm
          profile={editingProfile}
          onClose={handleProfileFormClose}
        />
      )}
    </div>
  );
};

export default VehicleProfilesPage;
